"use client";

import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "@/store/store";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useParams } from "react-router-dom";
import {
  Crown,
  TrendingUp,
  Users,
  Package,
  ShoppingBag,
  Database,
  Zap,
  Star,
  Check,
  X,
  ArrowUp,
  ArrowDown,
  Sparkles,
  Wallet,
} from "lucide-react";
import { SubscriptionUpgradeModal } from "@/components/modals/SubscriptionUpgradeModal";
import { fetchCurrentSubscriptionRequest } from "@/store/slices/subscription.slice";
import { getBalance, WalletBalance } from "@/services/walletService";

interface UsageMetric {
  label: string;
  current: number;
  limit: number;
  percentage: number;
  icon: React.ReactNode;
  color: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  billing: "monthly" | "yearly";
  popular?: boolean;
  current?: boolean;
  features: {
    category: string;
    items: Array<{
      name: string;
      included: boolean | string | number;
    }>;
  }[];
}

const Subscription = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { storeId } = useParams<{ storeId: string }>();

  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly",
  );
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<{
    id: string;
    name: string;
    price: number;
  } | null>(null);
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(
    null,
  );

  // Get subscription state from Redux
  const { currentPlan: reduxCurrentPlan } = useSelector(
    (state: RootState) => state.subscription,
  );

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchCurrentSubscriptionRequest());

    // Load wallet balance
    const loadWalletBalance = async () => {
      try {
        const response = await getBalance();
        setWalletBalance(response.data as WalletBalance);
      } catch (error) {
        console.error("Failed to load wallet balance:", error);
      }
    };

    loadWalletBalance();
  }, [dispatch, storeId]);

  // Subscription plans with proper hierarchy (order matters for upgrade/downgrade logic)
  const subscriptionPlans: SubscriptionPlan[] = [
    {
      id: "free",
      name: "Free",
      price: 0,
      billing: billingCycle,
      features: [
        {
          category: "Store Management",
          items: [
            { name: "Products", included: 10 },
            { name: "Orders per month", included: 50 },
            { name: "Storage", included: "500MB" },
            { name: "Custom domain", included: false },
            { name: "Multi-store", included: false },
          ],
        },
        {
          category: "Sales Features",
          items: [
            { name: "Payment gateways", included: 1 },
            { name: "Discount coupons", included: false },
            { name: "Abandoned cart recovery", included: false },
            { name: "Analytics dashboard", included: "Basic" },
            { name: "Email marketing", included: false },
            { name: "SEO tools", included: false },
          ],
        },
        {
          category: "Support & Features",
          items: [
            { name: "Email support", included: false },
            { name: "Priority support", included: false },
            { name: "Phone support", included: false },
            { name: "Dedicated manager", included: false },
            { name: "Custom integrations", included: false },
            { name: "API access", included: false },
          ],
        },
      ],
    },
    {
      id: "starter",
      name: "Starter",
      price: billingCycle === "monthly" ? 19 : 190,
      billing: billingCycle,
      features: [
        {
          category: "Store Management",
          items: [
            { name: "Products", included: 100 },
            { name: "Orders per month", included: 500 },
            { name: "Storage", included: "2GB" },
            { name: "Custom domain", included: false },
            { name: "Multi-store", included: false },
          ],
        },
        {
          category: "Sales Features",
          items: [
            { name: "Payment gateways", included: 2 },
            { name: "Discount coupons", included: true },
            { name: "Abandoned cart recovery", included: false },
            { name: "Analytics dashboard", included: "Basic" },
            { name: "Email marketing", included: "500/month" },
            { name: "SEO tools", included: "Basic" },
          ],
        },
        {
          category: "Support & Features",
          items: [
            { name: "Email support", included: true },
            { name: "Priority support", included: false },
            { name: "Phone support", included: false },
            { name: "Dedicated manager", included: false },
            { name: "Custom integrations", included: false },
            { name: "API access", included: "Basic" },
          ],
        },
      ],
    },
    {
      id: "professional",
      name: "Professional",
      price: billingCycle === "monthly" ? 49 : 490,
      billing: billingCycle,
      popular: true,
      features: [
        {
          category: "Store Management",
          items: [
            { name: "Products", included: 1000 },
            { name: "Orders per month", included: 5000 },
            { name: "Storage", included: "10GB" },
            { name: "Custom domain", included: true },
            { name: "Multi-store", included: "3 stores" },
          ],
        },
        {
          category: "Sales Features",
          items: [
            { name: "Payment gateways", included: "Unlimited" },
            { name: "Discount coupons", included: true },
            { name: "Abandoned cart recovery", included: true },
            { name: "Analytics dashboard", included: "Advanced" },
            { name: "Email marketing", included: "5000/month" },
            { name: "SEO tools", included: "Advanced" },
          ],
        },
        {
          category: "Support & Features",
          items: [
            { name: "Email support", included: true },
            { name: "Priority support", included: true },
            { name: "Phone support", included: false },
            { name: "Dedicated manager", included: false },
            { name: "Custom integrations", included: "5 integrations" },
            { name: "API access", included: "Full" },
          ],
        },
      ],
    },
    {
      id: "business",
      name: "Business",
      price: billingCycle === "monthly" ? 79 : 790,
      billing: billingCycle,
      features: [
        {
          category: "Store Management",
          items: [
            { name: "Products", included: 5000 },
            { name: "Orders per month", included: 25000 },
            { name: "Storage", included: "50GB" },
            { name: "Custom domain", included: true },
            { name: "Multi-store", included: "10 stores" },
          ],
        },
        {
          category: "Sales Features",
          items: [
            { name: "Payment gateways", included: "Unlimited" },
            { name: "Discount coupons", included: true },
            { name: "Abandoned cart recovery", included: true },
            { name: "Analytics dashboard", included: "Business" },
            { name: "Email marketing", included: "25000/month" },
            { name: "SEO tools", included: "Business" },
          ],
        },
        {
          category: "Support & Features",
          items: [
            { name: "Email support", included: true },
            { name: "Priority support", included: true },
            { name: "Phone support", included: true },
            { name: "Dedicated manager", included: false },
            { name: "Custom integrations", included: "20 integrations" },
            { name: "API access", included: "Full" },
          ],
        },
      ],
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: billingCycle === "monthly" ? 149 : 1490,
      billing: billingCycle,
      features: [
        {
          category: "Store Management",
          items: [
            { name: "Products", included: "Unlimited" },
            { name: "Orders per month", included: "Unlimited" },
            { name: "Storage", included: "500GB" },
            { name: "Custom domain", included: true },
            { name: "Multi-store", included: "Unlimited" },
          ],
        },
        {
          category: "Sales Features",
          items: [
            { name: "Payment gateways", included: "Unlimited" },
            { name: "Discount coupons", included: true },
            { name: "Abandoned cart recovery", included: true },
            { name: "Analytics dashboard", included: "Enterprise" },
            { name: "Email marketing", included: "Unlimited" },
            { name: "SEO tools", included: "Enterprise" },
          ],
        },
        {
          category: "Support & Features",
          items: [
            { name: "Email support", included: true },
            { name: "Priority support", included: true },
            { name: "Phone support", included: true },
            { name: "Dedicated manager", included: true },
            { name: "Custom integrations", included: "Unlimited" },
            { name: "API access", included: "Full + Webhooks" },
          ],
        },
      ],
    },
    {
      id: "white-label",
      name: "White Label",
      price: billingCycle === "monthly" ? 299 : 2990,
      billing: billingCycle,
      features: [
        {
          category: "Store Management",
          items: [
            { name: "Products", included: "Unlimited" },
            { name: "Orders per month", included: "Unlimited" },
            { name: "Storage", included: "2TB" },
            { name: "Custom domain", included: true },
            { name: "Multi-store", included: "Unlimited" },
          ],
        },
        {
          category: "Sales Features",
          items: [
            { name: "Payment gateways", included: "Unlimited" },
            { name: "Discount coupons", included: true },
            { name: "Abandoned cart recovery", included: true },
            { name: "Analytics dashboard", included: "White Label" },
            { name: "Email marketing", included: "Unlimited" },
            { name: "SEO tools", included: "White Label" },
          ],
        },
        {
          category: "Support & Features",
          items: [
            { name: "Email support", included: true },
            { name: "Priority support", included: true },
            { name: "Phone support", included: true },
            { name: "Dedicated manager", included: true },
            { name: "Custom integrations", included: "Unlimited" },
            { name: "API access", included: "Full + Custom" },
          ],
        },
      ],
    },
  ];

  // Get actual current plan details from the plans array
  const actualCurrentPlan = subscriptionPlans.find(
    (plan) => plan.id === (reduxCurrentPlan?.id || "free"),
  );

  // Fallback current plan data if API data not available yet
  const currentPlan = actualCurrentPlan || {
    id: "free",
    name: "Free",
    price: 0,
    billing: "monthly" as const,
    nextBilling: "N/A",
    status: "Active",
    features: [], // Add this to match SubscriptionPlan interface
  };

  // Usage metrics
  const usageMetrics: UsageMetric[] = [
    {
      label: "Products",
      current: 847,
      limit: 1000,
      percentage: 84.7,
      icon: <Package className="h-5 w-5" />,
      color: "bg-blue-500",
    },
    {
      label: "Orders",
      current: 2341,
      limit: 5000,
      percentage: 46.8,
      icon: <ShoppingBag className="h-5 w-5" />,
      color: "bg-green-500",
    },
    {
      label: "Customers",
      current: 567,
      limit: 2000,
      percentage: 28.4,
      icon: <Users className="h-5 w-5" />,
      color: "bg-purple-500",
    },
    {
      label: "Storage",
      current: 7.2,
      limit: 10,
      percentage: 72,
      icon: <Database className="h-5 w-5" />,
      color: "bg-orange-500",
    },
  ];

  const formatValue = (value: boolean | string | number) => {
    if (typeof value === "boolean") {
      return value ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <X className="h-4 w-4 text-red-500" />
      );
    }
    return value;
  };

  const getUpgradeAction = (planId: string) => {
    // Get current plan ID from Redux (real data) or fallback
    const currentPlanId = reduxCurrentPlan?.id || "free"; // Default to free if no current plan

    // Find indices in the plans array (order matters - lower index = lower tier)
    const currentIndex = subscriptionPlans.findIndex(
      (p) => p.id === currentPlanId,
    );
    const targetIndex = subscriptionPlans.findIndex((p) => p.id === planId);

    // If this is the current plan
    if (planId === currentPlanId) {
      return {
        label: "Current Plan",
        icon: <Check className="h-4 w-4" />,
        variant: "secondary" as const,
        className: "bg-primary/10 text-primary hover:bg-primary/10",
      };
    }

    // If target plan is higher in array (higher tier) = upgrade
    if (targetIndex > currentIndex) {
      return {
        label: "Upgrade",
        icon: <ArrowUp className="h-4 w-4" />,
        variant: "default" as const,
        className: "",
      };
    }

    // If target plan is lower in array (lower tier) = downgrade
    if (targetIndex < currentIndex) {
      return {
        label: "Downgrade",
        icon: <ArrowDown className="h-4 w-4" />,
        variant: "outline" as const,
        className:
          "border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20",
      };
    }

    // Fallback (shouldn't happen)
    return {
      label: "Select Plan",
      icon: <Check className="h-4 w-4" />,
      variant: "outline" as const,
      className: "",
    };
  };

  // Handle upgrade/downgrade button click
  const handlePlanAction = (
    planId: string,
    planName: string,
    planPrice: number,
  ) => {
    const currentPlanId = reduxCurrentPlan?.id || "free";

    if (planId === currentPlanId) return; // Can't change to same plan

    setSelectedPlan({
      id: planId,
      name: planName,
      price: planPrice,
    });
    setUpgradeModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="pl-4 mb-6">
        <h2 className="text-xl font-semibold">Subscription Management</h2>
        <p className="text-muted-foreground text-sm">
          Manage your subscription plan and monitor usage
        </p>
      </div>

      {/* Current Plan Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Plan Card */}
        <Card className="lg:col-span-1 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full -translate-y-16 translate-x-16"></div>
          <CardHeader className="relative">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">{currentPlan.name}</CardTitle>
                <CardDescription>Current Plan</CardDescription>
              </div>
              <Badge className="ml-auto bg-green-500/20 text-green-600 hover:bg-green-500/20">
                {"status" in currentPlan ? currentPlan.status : "Active"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold">${currentPlan.price}</span>
                <span className="text-muted-foreground">
                  /{currentPlan.billing}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Next billing:{" "}
                {"nextBilling" in currentPlan ? currentPlan.nextBilling : "N/A"}
              </p>
            </div>

            {/* Wallet Balance Section */}
            {walletBalance && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium">Wallet Balance</span>
                  </div>
                  <span className="font-semibold">
                    ${walletBalance.balance.toFixed(2)}
                  </span>
                </div>
                {walletBalance.pendingBalance > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    + ${walletBalance.pendingBalance.toFixed(2)} pending
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                <Sparkles className="h-4 w-4 mr-2" />
                Upgrade Plan
              </Button>
              <Button variant="outline" className="w-full">
                Manage Billing
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Usage Metrics */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Usage Overview
            </CardTitle>
            <CardDescription>
              Monitor your current usage across different limits
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {usageMetrics.map((metric) => (
                <div key={metric.label} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-8 w-8 ${metric.color}/10 rounded-lg flex items-center justify-center`}
                      >
                        <div
                          className={`${metric.color.replace("bg-", "text-")}`}
                        >
                          {metric.icon}
                        </div>
                      </div>
                      <span className="font-medium">{metric.label}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {metric.current} / {metric.limit}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <Progress value={metric.percentage} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{metric.percentage.toFixed(1)}% used</span>
                      <span
                        className={
                          metric.percentage > 80 ? "text-orange-500" : ""
                        }
                      >
                        {metric.percentage > 80
                          ? "Consider upgrading"
                          : "Good usage"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upsell Section */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-600" />
                Ready to Scale Your Business?
              </h3>
              <p className="text-muted-foreground">
                You&apos;re using 84.7% of your product limit. Upgrade to
                Enterprise for unlimited products and advanced features.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline">Learn More</Button>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Star className="h-4 w-4 mr-2" />
                Upgrade Now
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4 py-4">
        <span
          className={
            billingCycle === "monthly" ? "font-medium" : "text-muted-foreground"
          }
        >
          Monthly
        </span>
        <div
          onClick={() =>
            setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly")
          }
          className="relative w-16 h-8 bg-gray-200 dark:bg-muted border border-gray-300 dark:border-transparent rounded-full cursor-pointer transition-colors hover:bg-gray-300 dark:hover:bg-muted/80"
        >
          <div
            className={`absolute top-1 w-6 h-6 bg-primary rounded-full transition-transform ${
              billingCycle === "yearly" ? "translate-x-9" : "translate-x-1"
            }`}
          />
        </div>
        <span
          className={
            billingCycle === "yearly" ? "font-medium" : "text-muted-foreground"
          }
        >
          Yearly
        </span>
        {billingCycle === "yearly" && (
          <Badge className="bg-green-500/20 text-green-600 hover:bg-green-500/20">
            Save 20%
          </Badge>
        )}
      </div>

      {/* Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Compare Plans</CardTitle>
          <CardDescription>
            Choose the perfect plan for your business needs
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-4 font-medium sticky left-0 bg-background z-10 min-w-[200px]">
                    Features
                  </th>
                  {subscriptionPlans.map((plan) => (
                    <th
                      key={plan.id}
                      className="text-center p-4 min-w-[180px] relative"
                    >
                      <div className="space-y-2">
                        <h3 className="font-semibold text-lg">{plan.name}</h3>
                        <div className="text-2xl font-bold">
                          ${plan.price}
                          <span className="text-sm font-normal text-muted-foreground">
                            /{plan.billing}
                          </span>
                        </div>
                        {billingCycle === "yearly" && (
                          <p className="text-xs text-green-600">Save 20%</p>
                        )}
                        <div className="flex flex-col gap-1 items-center mt-2">
                          {plan.popular && (
                            <Badge className="bg-purple-500/20 text-purple-600 hover:bg-purple-500/20">
                              Most Popular
                            </Badge>
                          )}
                          {plan.id === (reduxCurrentPlan?.id || "free") && (
                            <Badge className="bg-primary/20 text-primary hover:bg-primary/20 border-primary/30">
                              Current Plan
                            </Badge>
                          )}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subscriptionPlans[0].features.map(
                  (featureCategory, categoryIndex) => (
                    <>
                      <tr
                        key={`category-${categoryIndex}`}
                        className="bg-muted/30"
                      >
                        <td
                          colSpan={subscriptionPlans.length + 1}
                          className="p-4 font-medium text-sm uppercase tracking-wider sticky left-0 bg-muted/30 z-10"
                        >
                          {featureCategory.category}
                        </td>
                      </tr>
                      {featureCategory.items.map((feature, featureIndex) => (
                        <tr
                          key={`feature-${categoryIndex}-${featureIndex}`}
                          className="border-b"
                        >
                          <td className="p-4 text-sm sticky left-0 bg-background z-10 border-r">
                            {feature.name}
                          </td>
                          {subscriptionPlans.map((plan) => (
                            <td
                              key={`${plan.id}-${categoryIndex}-${featureIndex}`}
                              className="p-4 text-center"
                            >
                              {formatValue(
                                plan.features[categoryIndex].items[featureIndex]
                                  .included,
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  ),
                )}
                <tr>
                  <td className="p-4 sticky left-0 bg-background z-10"></td>
                  {subscriptionPlans.map((plan) => {
                    const action = getUpgradeAction(plan.id);
                    return (
                      <td key={`action-${plan.id}`} className="p-4">
                        <Button
                          variant={action.variant}
                          className={`w-full ${action.className}`}
                          disabled={
                            plan.id === (reduxCurrentPlan?.id || "free")
                          }
                          onClick={() =>
                            handlePlanAction(plan.id, plan.name, plan.price)
                          }
                        >
                          {action.icon}
                          {action.label}
                        </Button>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Upgrade/Downgrade Modal */}
      {selectedPlan && (
        <SubscriptionUpgradeModal
          isOpen={upgradeModalOpen}
          onClose={() => {
            setUpgradeModalOpen(false);
            setSelectedPlan(null);
          }}
          targetPlanId={selectedPlan.id}
          targetPlanName={selectedPlan.name}
          targetPlanPrice={selectedPlan.price}
          billingCycle={billingCycle}
          walletBalance={walletBalance}
        />
      )}
    </div>
  );
};

export default Subscription;
